'use client';

import React from 'react';
import { CaretUp } from '@phosphor-icons/react';
import { Accordion as MuiAccordion, AccordionSummary, AccordionDetails, Box, Stack, Typography } from '@unity/cloud-ui';
import { useTheme } from '@unity/cloud-ui/styles';

export const AccordionGroup = (props) => {
  const { children } = props;
  const [expanded, setExpanded] = React.useState<number | false>(false);

  const handleChange = (panelIndex: number) => (_: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panelIndex : false);
  };

  return (
    <Box sx={({ vars }) => ({ borderRadius: '4px', border: `1px solid ${vars.palette.divider}` })}>
      {React.Children.map(children, (child, index) => {
        return React.cloneElement(child, {
          expanded: expanded === index,
          onChange: handleChange(index),
          isAccordionGroupChild: true,
        });
      })}
    </Box>
  );
};

export const Accordion = (props) => {
  const { title, children, expanded, isAccordionGroupChild = false, ...rest } = props;
  const { vars, spacing } = useTheme();

  const accordionSummarySx = {
    borderTopLeftRadius: '3px',
    borderTopRightRadius: '3px',
    '.MuiAccordionSummary-content.Mui-expanded': {
      margin: spacing(1.5, 0),
    },
    '&.Mui-expanded': {
      minHeight: '48px !important',
      ...(!isAccordionGroupChild && {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      }),
    },
    ...(!isAccordionGroupChild && {
      borderBottomLeftRadius: '3px',
      borderBottomRightRadius: '3px',
    }),
    ...(isAccordionGroupChild && expanded
      ? { backgroundColor: vars.palette.primary.lighter }
      : {
          '&:hover': {
            backgroundColor: vars.palette.primary.lighter,
          },
        }),
  };

  return (
    <MuiAccordion
      expanded={expanded}
      {...rest}
      disableGutters
      sx={{
        ...(!isAccordionGroupChild && { border: `1px solid ${vars.palette.divider}`, borderRadius: '4px' }),
        '& > .MuiAccordion-heading .MuiAccordionSummary-expandIconWrapper': {
          transform: 'rotate(-180deg)',
        },
        ...rest?.sx,
      }}
    >
      <AccordionSummary expandIcon={<CaretUp />} sx={accordionSummarySx}>
        <Typography variant="h5" component="div">
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 2, borderTop: `1px solid ${vars.palette.divider}` }}>
        <Stack gap={2}>{children}</Stack>
      </AccordionDetails>
    </MuiAccordion>
  );
};
